import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  CircularProgress,
  FormControl,
  FormLabel,
  MenuItem,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingIndicatorLogo } from '@advisorycloud/react-ui-components';
import { sendPushNotification } from '../../services/admin_services';

const TAG_OPTIONS = [
  { value: 'new-message', label: 'New message' },
  { value: 'advisor-idea', label: 'Advisor idea' },
  { value: 'company-current-goal', label: 'Company current goal' },
  { value: 'company-boardroom-update', label: 'Company boardroom update' },
  { value: 'new-survey', label: 'New survey' },
  { value: 'simple', label: 'Simple' },
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'advisor', label: 'Advisor' },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'free-trial', label: 'Free Trial' },
  { value: 'freemium', label: 'Freemium' },
];

const BILLING_CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'quarterly', label: 'Quarterly' },
];

const initialValues = {
  title: 'Demo notification',
  body: 'This is a test message',
  url: 'dashboard',
  // icon: 'app.advisorycloud.com/icon-192.png',
  image: '',
  // badge: '1',
  tag: 'simple',
  data: '{}',
  accountId: '',
  userId: '',
  accountType: '',
  paymentType: [],
  billingCycle: [],
  registrationDateFilter: 'all',
  registrationStartDate: '',
  registrationEndDate: '',
  emailContains: '',
  sendToAll: true,
};

const buildPayload = (values) => {
  const payload = {};

  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      payload[key] = value;
      return;
    }

    if (value === undefined || value === null) {
      return;
    }

    const trimmed = typeof value === 'string' ? value.trim() : value;

    if (Array.isArray(trimmed) && trimmed.length === 0) {
      return;
    }

    if (trimmed !== '') {
      payload[key] = trimmed;
    }
  });

  return payload;
};

const getErrorMessage = (error) => {
  if (error.errors && error.errors[0]?.message) {
    try {
      const parsed = JSON.parse(error.errors[0].message);
      return parsed?.message || error.errors[0].message;
    } catch (parseError) {
      return error.errors[0].message;
    }
  }

  return 'Error sending push notification';
};

function PushNotifications() {
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState({
    open: false,
    message: '',
    payload: null,
  });
  const [confirmSending, setConfirmSending] = useState(false);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      setFormValues((prev) => ({
        ...prev,
        [name]: checked,
        ...(name === 'sendToAll' && checked
          ? { accountId: '', userId: '', accountType: '' }
          : {}),
      }));
      return;
    }

    if (name === 'paymentType' || name === 'billingCycle') {
      setFormValues((prev) => ({
        ...prev,
        [name]: typeof value === 'string' ? value.split(',') : value,
      }));
      return;
    }

    if (name === 'registrationDateFilter') {
      setFormValues((prev) => ({
        ...prev,
        registrationDateFilter: value,
        ...(value === 'all'
          ? { registrationStartDate: '', registrationEndDate: '' }
          : {}),
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    const titleTrimmed = formValues.title.trim();
    const bodyTrimmed = formValues.body.trim();

    if (!titleTrimmed) {
      nextErrors.title = 'Title is required';
    } else if (titleTrimmed.length > 50) {
      nextErrors.title = 'Title must be 50 characters or less';
    }
    if (!bodyTrimmed) {
      nextErrors.body = 'Body is required';
    } else if (bodyTrimmed.length > 150) {
      nextErrors.body = 'Body must be 150 characters or less';
    }
    if (formValues.registrationDateFilter === 'range') {
      if (!formValues.registrationStartDate) {
        nextErrors.registrationStartDate = 'Start date is required';
      }
      if (!formValues.registrationEndDate) {
        nextErrors.registrationEndDate = 'End date is required';
      }
    }
    if (
      !formValues.sendToAll &&
      !formValues.accountId.trim() &&
      !formValues.userId.trim() &&
      !formValues.accountType.trim()
    ) {
      nextErrors.target = 'Set an account/user filter or enable "Send to all".';
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length) {
      return;
    }

    setSubmitting(true);
    const payload = buildPayload(formValues);

    try {
      const confirmResponse = await sendPushNotification({
        ...payload,
        confirmOnly: true,
      });
      const confirmationMessage =
        confirmResponse?.data?.sendPushNotification?.message ||
        'Confirm sending this push notification.';

      setConfirmation({
        open: true,
        message: confirmationMessage,
        payload,
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: getErrorMessage(error),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseConfirmation = () =>
    setConfirmation({
      open: false,
      message: '',
      payload: null,
    });

  const handleConfirmSend = async () => {
    if (!confirmation.payload) {
      handleCloseConfirmation();
      return;
    }

    setConfirmSending(true);
    setSubmitting(true);

    try {
      await sendPushNotification({
        ...confirmation.payload,
        confirmOnly: false,
      });
      setSnackbar({
        open: true,
        message: 'Push notification sent',
        severity: 'success',
      });
      handleCloseConfirmation();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: getErrorMessage(error),
        severity: 'error',
      });
    } finally {
      setConfirmSending(false);
      setSubmitting(false);
    }
  };

  const payloadPreview = buildPayload(formValues);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Push Notifications
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Fill in the content and define who will receive the notification.
      </Typography>

      <Paper elevation={0} sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle1" gutterBottom>
                Content
              </Typography>
              <TextField
                fullWidth
                required
                label="Title"
                name="title"
                value={formValues.title}
                onChange={handleChange}
                error={Boolean(errors.title)}
                helperText={errors.title || 'Max 50 characters'}
                inputProps={{ maxLength: 50 }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Detail"
                name="body"
                value={formValues.body}
                onChange={handleChange}
                error={Boolean(errors.body)}
                helperText={errors.body || 'Max 150 characters'}
                inputProps={{ maxLength: 150 }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="URL"
                name="url"
                value={formValues.url}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <Divider sx={{ my: 2 }} />
              {/* <TextField
                fullWidth
                label="Icon"
                name="icon"
                value={formValues.icon}
                onChange={handleChange}
                sx={{ mb: 2 }}
              /> */}
              {/* <TextField
                fullWidth
                label="Image"
                name="image"
                value={formValues.image}
                onChange={handleChange}
                sx={{ mb: 2 }}
              /> */}
              <Grid container spacing={2}>
                {/* <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Badge"
                    name="badge"
                    value={formValues.badge}
                    onChange={handleChange}
                  />
                </Grid> */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    select
                    label="Tag"
                    name="tag"
                    value={formValues.tag}
                    onChange={handleChange}
                  >
                    {TAG_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Data (JSON or plain text)"
                name="data"
                value={formValues.data}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" gutterBottom>
                Recipients
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.sendToAll}
                    onChange={handleChange}
                    name="sendToAll"
                  />
                }
                label="Send to all devices"
              />
              {formValues.sendToAll ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  It will be sent to all users with active tokens.
                </Alert>
              ) : (
                <Typography
                  variant="body2"
                  color={errors.target ? 'error' : 'textSecondary'}
                  sx={{ mb: 2 }}
                >
                  Optional filters: use accountId / userId / accountType or
                  enable "Send to all".
                  {errors.target ? ` ${errors.target}` : ''}
                </Typography>
              )}

              <TextField
                fullWidth
                label="Account ID"
                name="accountId"
                value={formValues.accountId}
                onChange={handleChange}
                disabled={formValues.sendToAll}
                error={Boolean(errors.target)}
                sx={{ mb: 2 }}
              />
              {/* <TextField
                fullWidth
                label="User ID"
                name="userId"
                value={formValues.userId}
                onChange={handleChange}
                disabled={formValues.sendToAll}
                error={Boolean(errors.target)}
                sx={{ mb: 2 }}
              /> */}
              <TextField
                fullWidth
                select
                label="Account Type"
                name="accountType"
                value={formValues.accountType}
                onChange={handleChange}
                disabled={formValues.sendToAll}
                error={Boolean(errors.target)}
                helperText="Select Company or Advisor"
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {ACCOUNT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                SelectProps={{ multiple: true }}
                label="Payment Type"
                name="paymentType"
                disabled={formValues.sendToAll}
                value={formValues.paymentType}
                onChange={handleChange}
                helperText="Pick one or more payment types"
                sx={{ mb: 2 }}
              >
                {PAYMENT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                SelectProps={{ multiple: true }}
                label="Billing Cycle"
                name="billingCycle"
                value={formValues.billingCycle}
                disabled={formValues.sendToAll}
                onChange={handleChange}
                helperText="Pick one or more billing cycles"
                sx={{ mb: 2 }}
              >
                {BILLING_CYCLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <FormLabel component="legend">Registration date</FormLabel>
                <RadioGroup
                  row
                  name="registrationDateFilter"
                  value={formValues.registrationDateFilter}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All Time"
                    disabled={formValues.sendToAll}
                  />
                  <FormControlLabel
                    value="range"
                    control={<Radio />}
                    label="Registration Date Range"
                    disabled={formValues.sendToAll}
                  />
                </RadioGroup>
              </FormControl>
              {formValues.registrationDateFilter === 'range' && (
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      name="registrationStartDate"
                      value={formValues.registrationStartDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(errors.registrationStartDate)}
                      helperText={errors.registrationStartDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      name="registrationEndDate"
                      value={formValues.registrationEndDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(errors.registrationEndDate)}
                      helperText={errors.registrationEndDate}
                    />
                  </Grid>
                </Grid>
              )}
              <TextField
                fullWidth
                label="Email Contains"
                name="emailContains"
                value={formValues.emailContains}
                disabled={formValues.sendToAll}
                onChange={handleChange}
                helperText="Send to users whose email contains this text"
                sx={{ mb: 2 }}
              />
              {/* 
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Payload to send
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  p: 2,
                  fontSize: 12,
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(payloadPreview, null, 2)}
              </Box> */}
            </Grid>
          </Grid>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={3}
            gap={2}
          >
            <Button
              variant="outlined"
              type="button"
              onClick={() => setFormValues(initialValues)}
              disabled={submitting}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={submitting}
            >
              Send notification
            </Button>
            {submitting && (
              <LoadingIndicatorLogo
                size={40}
                iconFontSize={15}
                iconRight={13}
              />
            )}
          </Box>
        </form>
      </Paper>
      <Dialog
        open={confirmation.open}
        onClose={confirmSending ? undefined : handleCloseConfirmation}
      >
        <DialogTitle sx={{ fontSize: '18px' }}>
          Confirm push notification
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">{confirmation.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConfirmation}
            disabled={confirmSending}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSend}
            disabled={confirmSending}
            variant="contained"
          >
            {confirmSending ? (
              <CircularProgress size={20} />
            ) : (
              'Send notification'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbar.open}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        autoHideDuration={4000}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default PushNotifications;
